import * as z from "zod";
import {
  creatorColumns,
  creatorGrantPrincipal,
  defaultCreatorGrants,
} from "../rbac/roles";
import { insertGrantsDeduped } from "../rbac/validation";
import { destinationSchema } from "./egressRouteShared";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema, generateId } from "../id";

const MAX_CERT_PEM_BYTES = 4096;

export const createNetworkSchema = z.object({
  id: clientGeneratableIdSchema,
  name: z.string().min(1),
  identifier: z.literal("tunnel"),
  destinations: z
    .array(destinationSchema.and(z.object({ spaceId: z.string().nullish() })))
    .min(1),
  spaceId: z.string().nullish(),
  clientCertPem: z.string().min(1).max(MAX_CERT_PEM_BYTES),
  clientCertSha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const createNetwork = mutator({
  schema: createNetworkSchema,
  resolve: {
    space: { kind: "space", from: "spaceId", optional: true },
    destinationSpaces: {
      kind: "space",
      from: "destinations",
      field: "spaceId",
    },
  },
  permissions: [
    { permission: "tenant:network_create", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  description:
    "Creates a tunnel network with its allowed destinations for the tenant.",
  interactionType: "create",
  fn: async ({
    tx,
    ctx,
    args: { id, name, identifier, destinations, space },
  }) => {
    const createdAt = Date.now();
    await tx.mutate.networks.insert({
      id,
      name,
      identifier,
      configuration: {},
      destinations: destinations.map(
        ({ spaceId: _spaceId, ...destination }) => destination
      ),
      metadata: {},
      createdAt,
      ...creatorColumns(ctx),
      lastTestAt: null,
      lastTestStatus: null,
      lastTestMessage: null,
      disabledAt: null,
      enforced: false,
      tenantId: ctx.tenantId,
    });
    for (const destination of destinations) {
      await tx.mutate.egressRoutes.insert({
        id: generateId(),
        networkId: id,
        spaceId:
          destination.spaceId === undefined
            ? (space?.id ?? null)
            : destination.spaceId,
        destinationType: destination.type,
        destination:
          destination.type === "cidr" ? destination.range : destination.pattern,
        enforced: false,
        createdAt,
        disabledAt: null,
        tenantId: ctx.tenantId,
      });
    }
    await insertGrantsDeduped(
      tx,
      defaultCreatorGrants("network", creatorGrantPrincipal(ctx)),
      id,
      ctx.tenantId
    );
  },
});
